import { richTextOrParagraphs, richTextToPlainText } from "@/lib/rich-text";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { queryOptions, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyContext } from "@/lib/volunteer.functions";

import { useEffect, useRef, useState } from "react";
import { resolveSource, track } from "@/lib/analytics";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Mail, MapPin, Package, Phone, Users } from "lucide-react";

import { SiteHeader } from "@/components/site/site-header";
import { ShareEvent } from "@/components/site/share-event";
import { PostClaimCta } from "@/components/site/post-claim-cta";
import { inviteFromSignup } from "@/lib/calendar-invite";
import { SHARE_CHANNELS, type ShareChannel } from "@/lib/share-channels";

import { SiteFooter } from "@/components/site/site-footer";
import { PublicErrorState } from "@/components/site/public-error";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldError, ValidationSummary } from "@/components/forms/field-error";
import { useInlineValidation } from "@/hooks/use-inline-validation";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import { T1D_RELATIONSHIPS, REFERRAL_SOURCES } from "@/lib/t1d-relationships";
import type { ClaimField } from "@/lib/claim-fields";

import { useSession } from "@/hooks/use-session";
import { claimSlotAsVolunteer, claimSlotPublic, getPublicEvent } from "@/lib/events.functions";
import { formatDay, formatDayShort, formatTime } from "./events.index";
import { reportHandledSync } from "@/lib/observability";
import { flow, flowResult } from "@/lib/flow";

const eventQuery = (slug: string) =>
  queryOptions({
    queryKey: ["events", "public", slug],
    queryFn: () => getPublicEvent({ data: { slug } }),
  });

export const Route = createFileRoute("/events/$slug")({
  loader: async ({ context, params }) => {
    const event = await context.queryClient.ensureQueryData(eventQuery(params.slug));
    if (!event) throw notFound();
    return event;
  },
  head: ({ loaderData }) => {
    const title = loaderData
      ? `${loaderData.title} | Volunteer Signup | T1D Warriors`
      : "Event Signup | T1D Warriors";
    const description = loaderData
      ? `${richTextToPlainText(loaderData.description) || `Claim a volunteer shift at ${loaderData.title}.`}`.slice(0, 155)
      : "Claim a volunteer shift at an upcoming T1D Warriors event.";
    const url = loaderData
      ? `https://volunteer.t1dw.org/events/${loaderData.slug}`
      : "https://volunteer.t1dw.org/events";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(loaderData?.share_image_url
          ? [
              { property: "og:image", content: loaderData.share_image_url },
              { name: "twitter:image", content: loaderData.share_image_url },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: loaderData
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Event",
                name: loaderData.title,
                description: richTextToPlainText(loaderData.description),
                startDate: loaderData.firstDate,
                endDate: loaderData.lastDate ?? loaderData.firstDate,
                eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
                location: {
                  "@type": "Place",
                  name: loaderData.location_name || "T1D Warriors event",
                  address: loaderData.address ?? "Northern Kentucky",
                },
                organizer: { "@type": "NGO", name: "T1D Warriors", url: "https://t1dw.org" },
                url,
              }),
            },
          ]
        : [],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="app-light min-h-[60vh]">
        <main
          className="mx-auto max-w-3xl px-4 pb-24 text-center sm:px-6"
          style={{ paddingTop: "calc(var(--site-hdr, 72px) + 5rem)" }}
        >
          <h1 className="t-h1 text-ice">That event is not open</h1>
          <p className="t-body mt-4">It may have wrapped up or is not published yet.</p>
          <Link to="/events" className="mt-8 inline-flex text-sm font-bold uppercase text-cyan">
            See all events
          </Link>
        </main>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <PublicErrorState
      error={error}
      route="/events/$slug"
      message="Refresh the page and the signup sheet should come back."
    />
  ),

  component: EventDetailPage,
});

function SummaryItem({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof CalendarDays;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-cyan" />
      <div className="min-w-0">
        <dt className="text-[0.8125rem] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </dt>
        <dd className="mt-1 text-[0.9375rem] font-semibold leading-snug text-ice">{children}</dd>
      </div>
    </div>
  );
}

function EventDetailPage() {
  const { slug } = Route.useParams();
  const { data: event } = useSuspenseQuery(eventQuery(slug));
  const [openSlot, setOpenSlot] = useState<string | null>(null);
  const [autoShare, setAutoShare] = useState<ShareChannel | null>(null);
  const openedSlots = useRef(new Set<string>());
  const claimedSlots = useRef(new Set<string>());

  useEffect(() => {
    const requested = new URLSearchParams(window.location.search).get("share");
    if (requested && (SHARE_CHANNELS as readonly string[]).includes(requested)) {
      setAutoShare(requested as ShareChannel);
    }
  }, []);

  useEffect(() => {
    track("page_view", { slug });
    const source = resolveSource();
    if (source === "email") track("email_link_click", { slug });

    const flushAbandoned = () => {
      for (const slotId of openedSlots.current) {
        if (!claimedSlots.current.has(slotId)) track("claim_abandoned", { slug, slotId });
      }
      openedSlots.current.clear();
    };
    window.addEventListener("pagehide", flushAbandoned);
    return () => {
      window.removeEventListener("pagehide", flushAbandoned);
      flushAbandoned();
    };
  }, [slug]);

  const [claimed, setClaimed] = useState<string[]>([]);
  const [openOnly, setOpenOnly] = useState(false);
  const [dayFilter, setDayFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<"all" | "shift" | "item">("all");

  if (!event) return null;


  const shiftCount = event.dates.reduce((total, date) => total + date.slots.length, 0);
  const first = event.dates[0]?.event_date;
  const last = event.dates[event.dates.length - 1]?.event_date;
  const dateRange = first
    ? last && last !== first
      ? `${formatDayShort(first)} to ${formatDayShort(last)}`
      : formatDay(first)
    : "";

  const openSpotCount = event.dates.reduce(
    (total, date) =>
      total +
      date.slots.reduce((sum, slot) => sum + Math.max(0, slot.capacity - slot.filled), 0),
    0,
  );
  const totalSpotCount = event.dates.reduce(
    (total, date) => total + date.slots.reduce((sum, slot) => sum + slot.capacity, 0),
    0,
  );
  const hasItems = event.dates.some((date) => date.slots.some((s) => s.slot_type === "item"));
  const filteredDates = event.dates
    .map((date) => ({
      ...date,
      slots: date.slots.filter((slot) => {
        if (openOnly && slot.capacity - slot.filled <= 0) return false;
        if (typeFilter === "shift" && slot.slot_type === "item") return false;
        if (typeFilter === "item" && slot.slot_type !== "item") return false;
        return true;
      }),
    }))
    .filter((date) => (dayFilter === "all" ? true : date.id === dayFilter))
    .filter((date) => date.slots.length > 0 || (!openOnly && typeFilter === "all"));
  const visibleSlotCount = filteredDates.reduce((t, d) => t + d.slots.length, 0);
  const filtersActive = openOnly || dayFilter !== "all" || typeFilter !== "all";


  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="app-light">
        <main
          className="mx-auto max-w-3xl px-4 pb-24 sm:px-6 lg:px-8"
          style={{ paddingTop: "calc(var(--site-hdr, 72px) + 2.5rem)" }}
        >

          <Link
            to="/events"
            className="t-meta -ml-1 inline-flex min-h-11 items-center px-1 hover:text-cyan"
          >
            Back to all events
          </Link>

        {event.program ? <p className="t-eyebrow mt-6">{event.program}</p> : null}
        <h1 className="t-h1 mt-3 text-balance text-ice">{event.title}</h1>

        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 sm:mt-6">
          <span className="flex items-center gap-2 text-[0.9375rem] text-foreground/80">
            <Users className="h-4 w-4 shrink-0 text-cyan" />
            {event.spotsOpen > 0 ? `${event.spotsOpen} spots open` : "All spots filled"}
          </span>
          {event.share_enabled !== false ? (
            <ShareEvent
              title={event.title}
              url={`https://volunteer.t1dw.org/events/${event.slug}`}
              channels={event.share_channels}
              message={event.share_message}
              autoOpen={autoShare}
              slug={event.slug}
            />
          ) : null}
        </div>

        <section aria-labelledby="event-summary" className="panel mt-8 p-5 sm:p-6">
          <h2 id="event-summary" className="t-eyebrow">
            Quick details
          </h2>
          <dl className="mt-4 grid gap-5 sm:grid-cols-2">
            <SummaryItem icon={CalendarDays} label="When">
              {dateRange || "Dates coming soon"}
              {shiftCount > 0 ? (
                <span className="mt-1 block text-sm text-muted-foreground">
                  {shiftCount} {shiftCount === 1 ? "shift" : "shifts"} across{" "}
                  {event.dates.length} {event.dates.length === 1 ? "day" : "days"}
                </span>
              ) : null}
            </SummaryItem>

            {event.location_name || event.address ? (
              <SummaryItem icon={MapPin} label="Where">
                {event.location_name}
                {event.address ? (
                  <span className="mt-1 block text-sm text-muted-foreground">{event.address}</span>
                ) : null}
                <a
                  className="-mx-1 mt-1 inline-flex min-h-11 items-center px-1 text-sm text-cyan hover:underline"
                  href={`https://maps.google.com/?q=${encodeURIComponent(
                    [event.location_name, event.address].filter(Boolean).join(" "),
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Get directions
                </a>
              </SummaryItem>
            ) : null}

            <SummaryItem icon={Users} label="Spots">
              {event.spotsOpen > 0
                ? `${event.spotsOpen} of ${event.spotsTotal ?? event.spotsOpen} still open`
                : "All spots filled"}
            </SummaryItem>

          </dl>
        </section>

        {richTextToPlainText(event.description ?? "").trim() ? (
          <section aria-labelledby="event-about" className="mt-12">
            <h2 id="event-about" className="t-h2 text-ice">
              About this event
            </h2>
            <div
              className="t-body event-rich-text mt-4 text-[1.0625rem] leading-[1.75]"
              dangerouslySetInnerHTML={{ __html: richTextOrParagraphs(event.description ?? "") }}
            />
          </section>
        ) : null}

        <section aria-labelledby="event-shifts" className="mt-14">
          <h2 id="event-shifts" className="t-h2 text-ice">
            Shifts and signups
          </h2>
          <p className="t-meta mt-2 text-sm">
            Pick a slot that fits your schedule and claim it. You will get a confirmation email with
            the details.
          </p>

          <div className="panel mt-6 flex flex-col gap-4 p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="t-eyebrow">Filter shifts</p>
              <span className="text-sm text-muted-foreground">
                {openSpotCount > 0
                  ? `${openSpotCount} of ${totalSpotCount} spots still open`
                  : "Every spot is filled"}
              </span>
            </div>

            <label className="-mx-2 flex min-h-11 cursor-pointer items-center gap-3 rounded-sm px-2 text-[0.9375rem] font-semibold text-foreground">
              <input
                type="checkbox"
                checked={openOnly}
                onChange={(e) => setOpenOnly(e.target.checked)}
                className="h-5 w-5 shrink-0 accent-cyan"
              />
              Only show shifts with spots left
            </label>

            {event.dates.length > 1 ? (
              <div className="filter-scroller">
                <FilterChip active={dayFilter === "all"} onClick={() => setDayFilter("all")}>
                  All days
                </FilterChip>
                {event.dates.map((date) => {
                  const open = date.slots.reduce(
                    (sum, s) => sum + Math.max(0, s.capacity - s.filled),
                    0,
                  );
                  return (
                    <FilterChip
                      key={date.id}
                      active={dayFilter === date.id}
                      onClick={() => setDayFilter(date.id)}
                    >
                      {formatDayShort(date.event_date)}
                      <span className="ml-1.5 text-xs opacity-80">
                        {open > 0 ? `${open} ${open === 1 ? "spot" : "spots"} open` : "full"}
                      </span>
                    </FilterChip>
                  );
                })}
              </div>
            ) : null}

            {hasItems ? (
              <div className="filter-scroller">
                <FilterChip active={typeFilter === "all"} onClick={() => setTypeFilter("all")}>
                  Everything
                </FilterChip>
                <FilterChip active={typeFilter === "shift"} onClick={() => setTypeFilter("shift")}>
                  Time shifts
                </FilterChip>
                <FilterChip active={typeFilter === "item"} onClick={() => setTypeFilter("item")}>
                  Items to bring
                </FilterChip>
              </div>
            ) : null}

            {filtersActive ? (
              <button
                type="button"
                onClick={() => {
                  setOpenOnly(false);
                  setDayFilter("all");
                  setTypeFilter("all");
                }}
                className="self-start text-sm font-semibold text-cyan hover:underline"
              >
                Clear filters
              </button>
            ) : null}
          </div>

          {filtersActive && visibleSlotCount === 0 ? (
            <p className="t-body mt-8 text-sm">
              Nothing matches those filters right now. Clear them to see every shift.
            </p>
          ) : null}

          <div className="mt-8 space-y-12">
            {filteredDates.map((date) => (

              <div key={date.id}>
                <h3 className="flex items-center gap-2.5 text-lg font-bold text-ice">
                  <CalendarDays className="h-5 w-5 shrink-0 text-cyan" />
                  {formatDay(date.event_date)}
                </h3>
                {date.note ? <p className="t-meta mt-2 text-sm">{date.note}</p> : null}
                <div className="mt-5 space-y-4">
                  {date.slots.length === 0 ? (
                    <p className="t-meta text-sm">No slots posted for this day yet.</p>
                  ) : null}
                  {date.slots.map((slot) => (
                    <SlotRow
                      key={slot.id}
                      slot={slot}
                      eventDate={date.event_date}
                      locationName={event.location_name ?? null}
                      address={event.address ?? null}
                      slug={slug}
                      event={event}
                      isOpen={openSlot === slot.id}
                      justClaimed={claimed.includes(slot.id)}
                      onToggle={() =>
                        setOpenSlot((cur) => {
                          const next = cur === slot.id ? null : slot.id;
                          if (next) {
                            openedSlots.current.add(slot.id);
                            track("signup_form_opened", { slug, slotId: slot.id });
                          }
                          return next;
                        })
                      }
                      onClaimed={() => {
                        claimedSlots.current.add(slot.id);
                        setClaimed((prev) => [...prev, slot.id]);
                        setOpenSlot(null);
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {event.public_contact_name || event.public_contact_email || event.public_contact_phone ? (
          <section aria-labelledby="event-contact" className="panel mt-14 p-5 sm:p-6">
            <h2 id="event-contact" className="t-h3 text-foreground">
              Questions about this event?
            </h2>
            <p className="t-body mt-1 text-[0.9375rem] text-muted-foreground">
              Reach out to {event.public_contact_name || "our team"} and we will help you out.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6">
              {event.public_contact_email ? (
                <a
                  className="-mx-2 inline-flex min-h-11 items-center gap-2 break-all rounded-sm px-2 text-[0.9375rem] font-semibold text-cyan hover:underline sm:mx-0 sm:px-0"
                  href={`mailto:${event.public_contact_email}`}
                >
                  <Mail className="h-4 w-4 shrink-0" />
                  {event.public_contact_email}
                </a>
              ) : null}
              {event.public_contact_phone ? (
                <a
                  className="-mx-2 inline-flex min-h-11 items-center gap-2 rounded-sm px-2 text-[0.9375rem] font-semibold text-cyan hover:underline sm:mx-0 sm:px-0"
                  href={`tel:${event.public_contact_phone.replace(/[^0-9+]/g, "")}`}
                >
                  <Phone className="h-4 w-4 shrink-0" />
                  {event.public_contact_phone}
                </a>
              ) : null}
            </div>
          </section>
        ) : null}

        </main>
      </div>
      <SiteFooter />

    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex min-h-11 shrink-0 snap-start items-center whitespace-nowrap rounded-sm border px-3.5 text-sm font-semibold transition-colors sm:min-h-9 ${
        active
          ? "border-cyan bg-cyan/10 text-cyan"
          : "border-border text-muted-foreground hover:border-cyan hover:text-cyan"
      }`}
    >
      {children}
    </button>
  );
}

type PublicSlot = {

  id: string;
  title: string;
  slot_type: string;
  start_time: string | null;
  end_time: string | null;
  capacity: number;
  notes: string | null;
  sort_order: number;
  filled: number;
  claimants: string[];
};

function SlotRow({
  slot,
  eventDate,
  locationName,
  address,
  slug,
  event,
  isOpen,
  justClaimed,
  onToggle,
  onClaimed,
}: {
  slot: PublicSlot;
  eventDate: string;
  locationName: string | null;
  address: string | null;
  slug: string;
  event: PublicEventShare;
  isOpen: boolean;
  justClaimed: boolean;
  onToggle: () => void;
  onClaimed: () => void;
}) {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const claimGuest = useServerFn(claimSlotPublic);
  const claimVolunteer = useServerFn(claimSlotAsVolunteer);
  const loadMyContext = useServerFn(getMyContext);
  // Signed-in volunteers should not retype what their profile already holds.
  // Prefill is a convenience: an empty/undefined response must degrade to a
  // blank form, never to a thrown "data is undefined" from react-query.
  const { data: myContext } = useQuery({
    queryKey: ["my-context", "claim-prefill"],
    queryFn: async () => (await loadMyContext()) ?? null,
    enabled: Boolean(user),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const myProfile = (myContext as { profile?: Record<string, any> | null } | undefined)?.profile ?? null;
  const knownT1d = (myProfile?.t1d_connection as string | null) ?? "";
  const knownShirtSize = (myProfile?.shirt_size as string | null) ?? "";

  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);

  const [claimedContact, setClaimedContact] = useState<{ email: string; full_name: string | null } | null>(null);
  const { errors, issues, fieldProps, focusField, resetErrors } = useInlineValidation();
  const cardRef = useRef<HTMLDivElement>(null);

  // On phones the opened form usually starts below the fold, so bring the card
  // into view under the fixed header the moment someone taps Claim.
  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return;
    const id = requestAnimationFrame(() => {
      const el = cardRef.current;
      if (!el) return;
      const headerVar = getComputedStyle(document.documentElement).getPropertyValue("--site-hdr");
      const header = Number.parseFloat(headerVar) || 72;
      const top = el.getBoundingClientRect().top + window.scrollY - header - 16;
      window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  const claimFields = event.claim_fields ?? [];
  const remaining = Math.max(0, slot.capacity - slot.filled);
  const full = remaining === 0;
  const isItem = slot.slot_type === "item";
  const time = slot.start_time
    ? `${formatTime(slot.start_time)}${slot.end_time ? ` - ${formatTime(slot.end_time)}` : ""}`
    : "Any time";

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // `busy` state only disables the button on the next render, so a fast
    // double tap can fire two claims. This ref closes that window.
    if (submitting.current) return;
    submitting.current = true;
    resetErrors();

    const form = new FormData(event.currentTarget);
    const fullName = [String(form.get("first_name") ?? ""), String(form.get("last_name") ?? "")]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(" ");
    const quantity = isItem ? Number(form.get("quantity") ?? 1) : 1;
    setBusy(true);
    track("signup_submitted", {
      slug,
      slotId: slot.id,
      metadata: { quantity, signedIn: Boolean(user) },
    });
    flow("claim.submit", { slug, slotId: slot.id, quantity, signedIn: Boolean(user) });
    try {
      const claim_answers: Record<string, unknown> = {};
      for (const field of claimFields) {
        claim_answers[field.key] =
          field.type === "checkbox"
            ? form.get(`cf_${field.key}`) === "on"
            : String(form.get(`cf_${field.key}`) ?? "");
      }
      const shared = {
        claim_answers,
        t1d_connection: String(form.get("t1d_connection") ?? ""),
        referral_source: String(form.get("referral_source") ?? ""),
        referral_name: String(form.get("referral_name") ?? ""),
      };
      if (user) {
        await claimVolunteer({
          data: {
            slot_id: slot.id,
            quantity,
            note: String(form.get("note") ?? ""),
            origin: window.location.origin,
            ...shared,
          },
        });
      } else {
        await claimGuest({
          data: {
            slot_id: slot.id,
            full_name: fullName,
            email: String(form.get("email") ?? ""),
            phone: String(form.get("phone") ?? ""),
            quantity,
            note: String(form.get("note") ?? ""),
            origin: window.location.origin,
            ...shared,
          },
        });
      }

      track("claim_completed", {
        slug,
        slotId: slot.id,
        metadata: { quantity, signedIn: Boolean(user) },
      });
      flowResult("claim.submit", true, { slug, slotId: slot.id });
      toast.success("You are on the list. Check your email for the details.");
      setClaimedContact({
        email: user?.email ?? String(form.get("email") ?? ""),
        full_name: user ? null : fullName,
      });
      onClaimed();
      queryClient.invalidateQueries({ queryKey: ["events", "public", slug] });
    } catch (error) {
      flowResult("claim.submit", false, { slug, slotId: slot.id });
      reportHandledSync(error, { feature: "events", action: "submit" });
      track("claim_failed", {
        slug,
        slotId: slot.id,
        metadata: { reason: error instanceof Error ? error.message.slice(0, 200) : "unknown" },
      });
      // Refresh availability too: the usual failure is "that spot just filled
      // up", and the card would otherwise keep showing it as open.
      queryClient.invalidateQueries({ queryKey: ["events", "public", slug] });
      toast.error(error instanceof Error ? error.message : "Could not claim that spot.");

    } finally {
      submitting.current = false;
      setBusy(false);
    }


  }

  return (
    <div ref={cardRef} className="panel scroll-mt-[calc(var(--site-hdr,72px)+1rem)] p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
        <div className="min-w-0">
          <p className="flex items-start gap-2 text-[1.0625rem] font-bold leading-snug tracking-normal text-ice">
            {isItem ? <Package className="mt-1 h-4 w-4 shrink-0 text-cyan" /> : null}
            {slot.title}
          </p>
          <p className="mt-2 text-sm font-semibold text-cyan">
            {isItem ? `Bring an item - ${slot.capacity} needed` : time}
          </p>
          {slot.notes ? (
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{slot.notes}</p>
          ) : null}
          {slot.claimants.length > 0 ? (
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
              Signed up: {slot.claimants.join(", ")}
            </p>
          ) : null}
        </div>

        <div className="flex w-full shrink-0 flex-col gap-3 sm:w-auto sm:items-end">
          <span
            className={`self-start rounded-sm px-2 py-1 text-[0.8125rem] font-semibold ${
              full ? "bg-muted text-muted-foreground" : "bg-cyan/10 text-cyan"
            }`}
          >
            {full
              ? "Full"
              : isItem
                ? `${remaining} of ${slot.capacity} left`
                : `${remaining} ${remaining === 1 ? "spot" : "spots"} left`}
          </span>

          {justClaimed ? (
            <span className="flex items-center gap-1.5 text-sm font-bold uppercase text-cyan">
              <CheckCircle2 className="h-4 w-4" /> Claimed
            </span>
          ) : (
            <Button
              type="button"
              onClick={onToggle}
              disabled={full}
              className="w-full uppercase sm:w-auto"
            >
              {isOpen ? "Cancel" : isItem ? "I can bring this" : "Claim this spot"}
            </Button>
          )}
        </div>
      </div>

      {isOpen && !full && !justClaimed ? (
        <form
          key={`claim-${slot.id}-${myProfile ? "prefilled" : "blank"}`}
          onSubmit={submit}
          className="mt-5 space-y-6 border-t border-border pt-5 sm:space-y-5"
        >

          <ValidationSummary issues={issues} onJump={focusField} />
          {user ? (
            <div
              className="flex items-start gap-2.5 border border-cyan/40 bg-cyan/5 px-3.5 py-3"
              style={{ borderRadius: 4 }}
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan" />
              <p className="text-sm font-semibold text-foreground">
                Signed in as {user.email}. Your name, contact info, and shirt size are already
                filled in from your volunteer profile. Just hit claim.
              </p>
            </div>
          ) : (
            <div
              className="flex items-start gap-2.5 border border-cyan/40 bg-cyan/5 px-3.5 py-3"
              style={{ borderRadius: 4 }}
            >
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-cyan" />
              <p className="text-sm text-foreground">
                <span className="font-semibold">Already an approved volunteer?</span>{" "}
                <Link to="/auth" className="font-semibold text-cyan underline underline-offset-2">
                  Sign in
                </Link>{" "}
                and this form fills itself in from your profile.
              </p>
            </div>
          )}
          {user ? null : (

            <div className="grid gap-5 sm:grid-cols-2 sm:gap-4">
              <div data-field-wrapper className="min-w-0 space-y-2">
                <Label htmlFor={`first-${slot.id}`}>First name</Label>
                <Input
                  id={`first-${slot.id}`}
                  required
                  maxLength={60}
                  autoComplete="given-name"
                  {...fieldProps("first_name", "First name")}
                />
                <FieldError id="first_name-error" message={errors.first_name} />
              </div>
              <div data-field-wrapper className="min-w-0 space-y-2">
                <Label htmlFor={`last-${slot.id}`}>Last name</Label>
                <Input
                  id={`last-${slot.id}`}
                  required
                  maxLength={60}
                  autoComplete="family-name"
                  {...fieldProps("last_name", "Last name")}
                />
                <FieldError id="last_name-error" message={errors.last_name} />
              </div>
              <div data-field-wrapper className="min-w-0 space-y-2">
                <Label htmlFor={`email-${slot.id}`}>Email</Label>
                <Input
                  id={`email-${slot.id}`}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  maxLength={255}
                  {...fieldProps("email", "Email")}
                />
                <FieldError id="email-error" message={errors.email} />
              </div>
              <div data-field-wrapper className="min-w-0 space-y-2">
                <Label htmlFor={`phone-${slot.id}`}>Phone</Label>
                <Input
                  id={`phone-${slot.id}`}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={40}
                  {...fieldProps("phone", "Phone")}
                />
                <FieldError id="phone-error" message={errors.phone} />
              </div>
            </div>
          )}

          {isItem ? (
            <div data-field-wrapper className="min-w-0 space-y-2 sm:max-w-[180px]">
              <Label htmlFor={`qty-${slot.id}`}>How many are you bringing?</Label>
              <Input
                id={`qty-${slot.id}`}
                type="number"
                inputMode="numeric"
                min={1}
                max={remaining}
                defaultValue={1}
                {...fieldProps("quantity", "How many are you bringing?")}
              />
              <FieldError id="quantity-error" message={errors.quantity} />
            </div>
          ) : null}

          {knownT1d ? (
            <input type="hidden" name="t1d_connection" value={knownT1d} />
          ) : (
            <div data-field-wrapper className="space-y-2">
              <Label htmlFor={`t1d-${slot.id}`}>
                What is your primary relationship to Type 1 Diabetes?
              </Label>
              <NativeSelect
                id={`t1d-${slot.id}`}
                required
                defaultValue=""
                {...fieldProps("t1d_connection", "What is your primary relationship to Type 1 Diabetes?")}
              >
                <option value="" disabled>
                  Select one
                </option>
                {T1D_RELATIONSHIPS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </NativeSelect>
              <FieldError id="t1d_connection-error" message={errors.t1d_connection} />
            </div>
          )}

          {user ? null : (
            <div className="grid gap-5 sm:grid-cols-2 sm:gap-4">
              <div data-field-wrapper className="min-w-0 space-y-2">
                <Label htmlFor={`ref-src-${slot.id}`}>How did you hear about this event?</Label>
                <NativeSelect
                  id={`ref-src-${slot.id}`}
                  defaultValue=""
                  {...fieldProps("referral_source", "How did you hear about this event?")}
                >
                  <option value="">Prefer not to say</option>
                  {REFERRAL_SOURCES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              <div data-field-wrapper className="min-w-0 space-y-2">
                <Label htmlFor={`ref-name-${slot.id}`}>Who referred you? (optional)</Label>
                <Input
                  id={`ref-name-${slot.id}`}
                  maxLength={160}
                  {...fieldProps("referral_name", "Who referred you?")}
                />
              </div>
            </div>
          )}


          {claimFields.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 sm:gap-4">
              {claimFields.map((field) => (
                <ClaimFieldInput
                  key={field.key}
                  field={field}
                  slotId={slot.id}
                  defaultValue={field.key === "shirt_size" ? knownShirtSize : ""}
                />
              ))}
            </div>
          ) : null}

          <div data-field-wrapper className="space-y-2">
            <Label htmlFor={`note-${slot.id}`}>Anything we should know?</Label>
            <Textarea id={`note-${slot.id}`} rows={2} maxLength={1000} {...fieldProps("note", "Anything we should know?")} />
          </div>


          {!user ? (
            <p className="t-meta">
              We will email you a confirmation for this shift. Want to do more with us? Apply to
              volunteer and our team will set up your account.
            </p>

          ) : null}



          <div className="sticky bottom-0 -mx-5 -mb-5 border-t border-border bg-card/95 px-5 py-3 backdrop-blur sm:static sm:mx-0 sm:mb-0 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none">
            <Button type="submit" disabled={busy} className="w-full uppercase sm:w-auto">
              {busy ? "Claiming..." : "Confirm my spot"}
            </Button>
          </div>
        </form>
      ) : null}

      {justClaimed ? (
        <PostClaimCta
          eventId={event.id}
          slug={slug}
          title={event.title}
          shareEnabled={event.share_enabled !== false}
          shareChannels={event.share_channels}
          shareMessage={event.share_message}
          email={claimedContact?.email ?? null}
          fullName={claimedContact?.full_name ?? null}
          calendarInvite={inviteFromSignup({
            eventTitle: event.title,
            slotTitle: slot.title,
            slotType: slot.slot_type,
            date: eventDate,
            startTime: slot.start_time,
            endTime: slot.end_time,
            locationName,
            address,
            eventUrl:
              typeof window !== "undefined"
                ? `${window.location.origin}/events/${slug}`
                : `https://volunteer.t1dw.org/events/${slug}`,
          })}
        />
      ) : null}
    </div>
  );
}

function ClaimFieldInput({
  field,
  slotId,
  defaultValue = "",
}: {
  field: ClaimField;
  slotId: string;
  defaultValue?: string;
}) {
  const id = `cf-${field.key}-${slotId}`;
  const name = `cf_${field.key}`;
  const label = `${field.label}${field.required ? "" : " (optional)"}`;

  if (field.type === "checkbox") {
    return (
      <div className="min-w-0 space-y-2 sm:col-span-2">
        <label
          htmlFor={id}
          className="flex min-h-11 items-start gap-3 py-1 text-[0.9375rem] leading-relaxed"
        >
          <input
            id={id}
            name={name}
            type="checkbox"
            required={field.required}
            className="mt-0.5 h-5 w-5 shrink-0 rounded-sm border border-input accent-cyan"
          />
          <span>{field.label}</span>
        </label>
        {field.help ? <p className="t-meta">{field.help}</p> : null}
      </div>
    );
  }

  return (
    <div className={`min-w-0 space-y-2 ${field.type === "textarea" ? "sm:col-span-2" : ""}`}>
      <Label htmlFor={id}>{label}</Label>
      {field.type === "select" ? (
        <NativeSelect id={id} name={name} required={field.required} defaultValue={defaultValue}>
          <option value="" disabled={field.required}>
            {field.required ? "Select one" : "Prefer not to say"}
          </option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </NativeSelect>
      ) : field.type === "textarea" ? (
        <Textarea id={id} name={name} rows={2} maxLength={1000} required={field.required} />
      ) : (
        <Input id={id} name={name} maxLength={200} required={field.required} />
      )}
      {field.help ? <p className="t-meta">{field.help}</p> : null}
    </div>
  );
}

type PublicEventShare = {
  id: string;
  title: string;
  claim_fields?: ClaimField[];
  share_enabled?: boolean | null;
  share_channels?: string[] | null;
  share_message?: string | null;
};
